import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { apiService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  Plus, Search, Edit2, Trash2, Scale, Activity, AlertTriangle, 
  CheckCircle, Clock, Building2, MapPin, User, Calendar, Settings, Eye, Zap,
  Wifi, Bluetooth, Radio, TrendingUp, BarChart3, Printer, QrCode, Copy, Wrench
} from 'lucide-react';
import QRCode from 'qrcode';
import { SmartScale } from '../types';
import Modal from './Modal';

const SmartScalesPage: React.FC = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCalibrationHistory, setShowCalibrationHistory] = useState(false);
  const [showCalibrationModal, setShowCalibrationModal] = useState(false);
  const [editingScale, setEditingScale] = useState<SmartScale | null>(null);
  const [selectedScale, setSelectedScale] = useState<SmartScale | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterProtocol, setFilterProtocol] = useState<string>('all');
  const [scalesData, setScalesData] = useState<SmartScale[]>([]);
  const [companiesData, setCompaniesData] = useState<any[]>([]);
  const [locationsData, setLocationsData] = useState<any[]>([]);
  const [usersData, setUsersData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [calibrationHistory, setCalibrationHistory] = useState<any[]>([]);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [selectedScaleForLabel, setSelectedScaleForLabel] = useState<SmartScale | null>(null);
  const [labelSettings, setLabelSettings] = useState({
    width: 100,
    height: 75,
    orientation: 'portrait',
    fontSize: 8,
    margin: 2
  });

  const [calibrationData, setCalibrationData] = useState({
    calibration_type: 'manual',
    calibration_company: '',
    calibration_number: '',
    calibration_date: new Date().toISOString().split('T')[0],
    next_calibration_date: '',
    notes: '',
    status: 'completed'
  });
  
  const [scaleData, setScaleData] = useState({
    scale_name: '',
    brand: '',
    model: '',
    serial_number: '',
    mac_address: '',
    ip_address: '',
    max_capacity_kg: 1000,
    precision_grams: 100,
    location_id: 0,
    company_id: 0,
    installation_date: '',
    calibration_date: '',
    next_calibration: '',
    status: 'active' as SmartScale['status'],
    communication_protocol: 'wifi' as SmartScale['communication_protocol'],
    assigned_admin_id: 0,
    purchase_price: 0,
    currency: 'TRY' as 'TRY' | 'USD' | 'EUR',
    notes: ''
  });

  // Load data on component mount
  React.useEffect(() => {
    loadData();
  }, []);

  // Bildirimden belirli bir terazi açma
  React.useEffect(() => {
    const handler = async (e: CustomEvent) => {
      if (e.detail?.page !== 'scales' || !e.detail?.entityId) return;
      try {
        const scale = await apiService.getSmartScale(e.detail.entityId);
        if (scale) { setSelectedScale(scale); setShowDetailModal(true); }
      } catch (err) { console.error('[openEntityById smartscales]', err); }
    };
    window.addEventListener('openEntityById', handler as EventListener);
    return () => window.removeEventListener('openEntityById', handler as EventListener);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Tüm kullanıcıları getir (admin, superadmin, teknisyen)
      const usersPromise = apiService.getUsers().catch(() => []);
        
      const [scalesResponse, companiesResponse, locationsResponse, usersResponse] = await Promise.all([
        apiService.getSmartScales(),
        apiService.getCompanies(),
        apiService.getLocations(),
        usersPromise
      ]);
      setScalesData(Array.isArray(scalesResponse) ? scalesResponse : []);
      setCompaniesData(Array.isArray(companiesResponse) ? companiesResponse : []);
      setLocationsData(Array.isArray(locationsResponse) ? locationsResponse : []);
      setUsersData(Array.isArray(usersResponse) ? usersResponse.filter(u => u.role === 'admin' || u.role === 'superadmin' || u.role === 'teknisyen') : []);
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(t('common.error'), t('scale.errorData'));
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { value: 'active', label: t('scale.statusActive'), color: 'bg-green-100 text-green-800' },
    { value: 'inactive', label: t('scale.statusInactive'), color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' },
    { value: 'maintenance', label: t('scale.statusMaintenance'), color: 'bg-yellow-100 text-yellow-800' },
    { value: 'calibration_needed', label: t('scale.statusCalibrationNeeded'), color: 'bg-orange-100 text-orange-800' }
  ];

  const healthOptions = [
    { value: 'healthy', label: t('scale.healthHealthy'), color: 'bg-green-100 text-green-800' },
    { value: 'warning', label: t('scale.healthWarning'), color: 'bg-yellow-100 text-yellow-800' },
    { value: 'critical', label: t('scale.healthCritical'), color: 'bg-red-100 text-red-800' },
    { value: 'unknown', label: t('scale.healthUnknown'), color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' }
  ];

  const protocolOptions = [
    { value: 'wifi', label: 'Wi-Fi', icon: Wifi },
    { value: 'bluetooth', label: 'Bluetooth', icon: Bluetooth },
    { value: 'cellular', label: 'Cellular', icon: Radio },
    { value: 'ethernet', label: 'Ethernet', icon: Zap }
  ];

  const filteredScales = scalesData.filter(scale => {
    const matchesSearch = String(scale.scale_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         String(scale.model || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         String(scale.serial_number || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || scale.status === filterStatus;
    const matchesProtocol = filterProtocol === 'all' || scale.communication_protocol === filterProtocol;
    
    return matchesSearch && matchesStatus && matchesProtocol;
  });

  const handleEdit = (scale: SmartScale) => {
    setEditingScale(scale);
    setScaleData({
      scale_name: scale.scale_name || '',
      brand: scale.brand || '',
      model: scale.model || '',
      serial_number: scale.serial_number || '',
      mac_address: scale.mac_address || '',
      ip_address: scale.ip_address || '',
      max_capacity_kg: scale.max_capacity_kg || 1000,
      precision_grams: scale.precision_grams || 100,
      location_id: scale.location_id || 0,
      company_id: scale.company_id || 0,
      installation_date: scale.installation_date || '',
      calibration_date: scale.calibration_date || '',
      next_calibration: scale.next_calibration || '',
      status: scale.status || 'active',
      communication_protocol: scale.communication_protocol || 'wifi',
      assigned_admin_id: scale.assigned_admin_id || 0,
      purchase_price: scale.purchase_price || 0,
      currency: scale.currency || 'TRY',
      notes: scale.notes || ''
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingScale(null);
    setScaleData({
      scale_name: '',
      brand: '',
      model: '',
      serial_number: '',
      mac_address: '',
      ip_address: '',
      max_capacity_kg: 1000,
      precision_grams: 100,
      location_id: 0,
      company_id: 0,
      installation_date: '',
      calibration_date: '',
      next_calibration: '',
      status: 'active',
      communication_protocol: 'wifi',
      assigned_admin_id: 0,
      purchase_price: 0,
      currency: 'TRY',
      notes: ''
    });
    setShowModal(true);
  };

  const handleCopy = (scale: SmartScale) => {
    setEditingScale(null);
    setScaleData({
      scale_name: `${scale.scale_name} - Kopya`,
      brand: scale.brand,
      model: scale.model,
      serial_number: '',
      mac_address: '',
      ip_address: '',
      max_capacity_kg: scale.max_capacity_kg,
      precision_grams: scale.precision_grams,
      location_id: scale.location_id,
      company_id: scale.company_id,
      installation_date: '',
      calibration_date: '',
      next_calibration: '',
      status: 'active',
      communication_protocol: scale.communication_protocol,
      assigned_admin_id: scale.assigned_admin_id,
      purchase_price: scale.purchase_price,
      currency: scale.currency,
      notes: scale.notes
    });
    setShowModal(true);
  };

  const checkDuplicateName = (name: string, excludeId?: number) => {
    return scalesData.some(scale => 
      String(scale.scale_name || '').toLowerCase() === String(name || '').toLowerCase() && 
      scale.id !== excludeId
    );
  };

  const checkDuplicateIP = (ip: string, excludeId?: number) => {
    if (!ip) return false;
    return scalesData.some(scale => 
      scale.ip_address && 
      String(scale.ip_address || '').toLowerCase() === String(ip || '').toLowerCase() && 
      scale.id !== excludeId
    );
  };

  const handleSave = async () => {
    if (!scaleData.scale_name || !scaleData.brand || !scaleData.model || !scaleData.serial_number) {
      showGlobalNotification.error(t('common.error'), t('scale.errorRequired'));
      return;
    }

    // Check for duplicate name
    if (checkDuplicateName(scaleData.scale_name, editingScale?.id)) {
      showGlobalNotification.error(t('common.error'), t('scale.errorDuplicateName'));
      return;
    }

    // Check for duplicate IP address
    if (scaleData.ip_address && checkDuplicateIP(scaleData.ip_address, editingScale?.id)) {
      showGlobalNotification.error(t('common.error'), t('scale.errorDuplicateIP'));
      return;
    }
    
    try {
      if (editingScale) {
        // Update existing scale
        await apiService.updateSmartScale(editingScale.id, scaleData);
        showGlobalNotification.success(t('common.success'), t('scale.successUpdated'));
      } else {
        // Add new scale
        await apiService.createSmartScale(scaleData);
        showGlobalNotification.success(t('common.success'), t('scale.successCreated'));
      }

      // Reload data
      await loadData();
      setShowModal(false);
    } catch (error: any) {
      console.error('Error saving smart scale:', error);
      showGlobalNotification.error(t('common.error'), error.message || t('scale.errorSave'));
    }
  };

  const handleDelete = async (id: number) => {
    const scaleToDelete = scalesData.find(s => s.id === id);
    if (!scaleToDelete) return;
    
    if (confirm(t('scale.confirmDelete'))) {
      try {
        await apiService.deleteSmartScale(id);
        await loadData();
        showGlobalNotification.success(t('common.success'), t('scale.successDeleted'));
      } catch (error: any) {
        console.error('Error deleting smart scale:', error);
        showGlobalNotification.error(t('common.error'), error.message || t('scale.errorDelete'));
      }
    }
  };

  const showScaleDetail = (scale: SmartScale) => {
    setSelectedScale(scale);
    setShowDetailModal(true);
  };

  const handleCalibration = async (scale: SmartScale) => {
    setSelectedScale(scale);
    setCalibrationData({
      calibration_type: 'manual',
      calibration_company: '',
      calibration_number: '',
      calibration_date: new Date().toISOString().split('T')[0],
      next_calibration_date: '',
      notes: '',
      status: 'completed'
    });
    setShowCalibrationModal(true);
  };

  const handleCalibrationHistory = async (scale: SmartScale) => {
    setSelectedScale(scale);
    await loadCalibrationHistory(scale.id);
    setShowCalibrationHistory(true);
  };

  const loadCalibrationHistory = async (scaleId: number) => {
    try {
      const history = await apiService.getScaleCalibrationHistory(scaleId);
      setCalibrationHistory(history);
    } catch (error: any) {
      console.error('Error loading calibration history:', error);
      showGlobalNotification.error(t('common.error'), t('scale.errorCalibHistory'));
      setCalibrationHistory([]);
    }
  };

  const handleCalibrationSave = async () => {
    if (!selectedScale) return;
    
    if (!calibrationData.calibration_company || !calibrationData.calibration_number) {
      showGlobalNotification.error(t('common.error'), t('scale.errorCalibRequired'));
      return;
    }
    
    try {
      await apiService.recordScaleCalibration(selectedScale.id, calibrationData);
      
      // Reload calibration history
      await loadCalibrationHistory(selectedScale.id);
      
      // Reload scales data to update calibration dates
      await loadData();
      
      setShowCalibrationModal(false);
      showGlobalNotification.success(t('common.success'), t('scale.successCalibSaved'));
    } catch (error: any) {
      console.error('Error recording calibration:', error);
      showGlobalNotification.error(t('common.error'), error.message || t('scale.errorCalibSave'));
    }
  };

  const handleCalibrationConfirm = async () => {
    if (!selectedScale) return;
    
    const notes = prompt(t('scale.calibNotesPrompt'));
    if (notes === null) return; // User cancelled

    try {
      const calibrationData = {
        calibration_type: 'manual',
        notes: notes || t('scale.calibDefaultNotes'),
        status: 'completed'
      };

      await apiService.recordScaleCalibration(selectedScale.id, calibrationData);

      // Reload calibration history
      await loadCalibrationHistory(selectedScale.id);

      // Reload scales data to update calibration dates
      await loadData();

      showGlobalNotification.success(t('common.success'), t('scale.successCalibSaved'));
    } catch (error: any) {
      console.error('Error recording calibration:', error);
      showGlobalNotification.error(t('common.error'), error.message || t('scale.errorCalibSave'));
    }
  };

  const getStatusColor = (status: string) => {
    return statusOptions.find(s => s.value === status)?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const getHealthIcon = (health: string) => {
    switch (health) {
      case 'healthy':
        return CheckCircle;
      case 'warning':
        return AlertTriangle;
      case 'critical':
        return AlertTriangle;
      default:
        return Clock;
    }
  };

  const getProtocolIcon = (protocol: string) => {
    return protocolOptions.find(p => p.value === protocol)?.icon || Wifi;
  };

  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'TRY': return '₺';
      default: return '₺';
    }
  };

  const isCalibrationDue = (nextCalibration: string) => {
    if (!nextCalibration) return false;
    const dueDate = new Date(nextCalibration);
    const today = new Date();
    const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilDue <= 30;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR');
  };

  const generateScaleQRCode = async (scale: SmartScale): Promise<string> => {
    // QR kod için sadece URL kullan - çoğu QR okuyucu JSON formatını anlamaz
    const qrCodeContent = `${window.location.origin}/scale-details/${scale.id}`;

    // Debug: QR kod içeriğini logla
    console.log('🔍 QR Kod İçeriği:', qrCodeContent);

    try {
      const qrCodeDataURL = await QRCode.toDataURL(qrCodeContent, {
        width: 100,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      return qrCodeDataURL;
    } catch (error) {
      console.error('QR kod oluşturma hatası:', error);
      throw error;
    }
  };

  const printScaleLabel = async (scale: SmartScale) => {
    try {
      const qrCodeDataURL = await generateScaleQRCode(scale);
      
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;

      printWindow.document.write(generateScaleLabelHTML(scale, qrCodeDataURL));
      printWindow.document.close();

      // Wait for content to load then print
      setTimeout(() => {
        printWindow.print();
      }, 1000);
      
    } catch (error) {
      console.error('Etiket yazdırma hatası:', error);
      showGlobalNotification.error(t('common.error'), t('scale.errorLabel'));
    }
  };

  const generateScaleLabelHTML = (scale: SmartScale, qrCodeDataURL: string) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${t('scale.labelModalTitle')} - ${scale.scale_name}</title>
        <style>
          @page {
            size: A4;
            margin: 10mm;
          }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
          }
          .page {
            display: grid;
            grid-template-columns: 1fr 1fr;
            grid-template-rows: 1fr 1fr;
            gap: 10mm;
            height: 267mm;
            page-break-after: always;
          }
          .page:last-child {
            page-break-after: avoid;
          }
          .label {
            border: 2px solid #333;
            border-radius: 8px;
            padding: 6mm;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            background: white;
            position: relative;
            overflow: hidden;
          }
          .inventory-number {
            background: white;
            border: 1px solid #ccc;
            border-radius: 4px;
            padding: 3mm;
            margin-bottom: 3mm;
            width: 100%;
            box-sizing: border-box;
          }
          .inventory-title {
            font-size: 16px;
            font-weight: bold;
            color: #666;
            margin-bottom: 2mm;
          }
          .inventory-value {
            font-size: 26px;
            font-weight: bold;
            color: #333;
            font-family: monospace;
            letter-spacing: 1px;
          }
          .device-name {
            font-size: 24px;
            font-weight: bold;
            color: #333;
            margin-bottom: 3mm;
          }
          .qr-code {
            width: 40mm;
            height: 40mm;
            margin-bottom: 3mm;
            border-radius: 2px;
          }
          .device-info {
            font-size: 26px;
            color: #666;
            line-height: 1.3;
          }
        </style>
      </head>
      <body>
        <div class="page">
          <div class="label">
            <div class="inventory-number">
              <div class="inventory-title">${t('scale.printScaleName')}</div>
              <div class="inventory-value">${scale.scale_name}</div>
            </div>
            <div class="device-name">${scale.brand} ${scale.model}</div>
            <img class="qr-code" src="${qrCodeDataURL}" alt="QR Code" />
            <div class="device-info">
              ${scale.ip_address ? `🌐 ${scale.ip_address}<br>` : ''}
              ${scale.serial_number ? `📋 ${scale.serial_number}<br>` : ''}
              ${scale.max_capacity_kg ? `⚖️ ${scale.max_capacity_kg} kg` : ''}
            </div>
          </div>
          <div class="label" style="border: none;"></div>
          <div class="label" style="border: none;"></div>
          <div class="label" style="border: none;"></div>
        </div>
      </body>
      </html>
    `;
  };

  const openLabelModal = (scale: SmartScale) => {
    setSelectedScaleForLabel(scale);
    setShowLabelModal(true);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('scale.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{t('scale.subtitle')}</p>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center justify-center px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors min-h-[44px] text-sm sm:text-base w-full sm:w-auto"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('scale.newScale')}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('scale.statTotal')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{scalesData.length}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Scale className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('scale.statActive')}</p>
              <p className="text-2xl font-bold text-green-600">
                {scalesData.filter(s => s.status === 'active').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('scale.statCalibNeeded')}</p>
              <p className="text-2xl font-bold text-orange-600">
                {scalesData.filter(s => s.status === 'calibration_needed' || isCalibrationDue(s.next_calibration || '')).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Settings className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('scale.statDailyMeasurement')}</p>
              <p className="text-2xl font-bold text-primary">
                {scalesData.reduce((sum, s) => sum + (s.daily_usage_count || 0), 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-primary" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder={t('scale.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent w-full"
          />
        </div>
        
        <select
          value={filterProtocol}
          onChange={(e) => setFilterProtocol(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
        >
          <option value="all">{t('scale.filterAllProtocols')}</option>
          {protocolOptions.map(protocol => (
            <option key={protocol.value} value={protocol.value}>{protocol.label}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
        >
          <option value="all">{t('scale.filterAllStatuses')}</option>
          {statusOptions.map(status => (
            <option key={status.value} value={status.value}>{status.label}</option>
          ))}
        </select>
      </div>

      {/* Scales Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('scale.colScale')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('scale.colLocation')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('scale.colStatus')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('scale.colProtocol')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('scale.colIp')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('scale.colCapacity')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('scale.colCalibration')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('scale.colActions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredScales.map((scale) => {
                const HealthIcon = getHealthIcon(scale.health_status);
                const ProtocolIcon = getProtocolIcon(scale.communication_protocol);
                const calibrationDue = isCalibrationDue(scale.next_calibration || '');
                
                return (
                  <tr key={scale.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                          <Scale className="w-4 h-4 text-orange-600" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {scale.scale_name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{scale.brand} {scale.model}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Building2 className="w-4 h-4 text-gray-400 mr-1" />
                        <div className="text-sm text-gray-900 dark:text-gray-100">{scale.company?.name}</div>
                      </div>
                      <div className="flex items-center mt-1">
                        <MapPin className="w-4 h-4 text-gray-400 mr-1" />
                        <div className="text-sm text-gray-500 dark:text-gray-400">{scale.location?.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <HealthIcon className="w-4 h-4 text-gray-400 mr-2" />
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          statusOptions.find(s => s.value === scale.status)?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                        }`}>
                          {statusOptions.find(s => s.value === scale.status)?.label}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <ProtocolIcon className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {protocolOptions.find(p => p.value === scale.communication_protocol)?.label}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100 font-mono">
                        {scale.ip_address || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{scale.max_capacity_kg} kg</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">±{scale.precision_grams}g</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {calibrationDue ? (
                        <div className="flex items-center">
                          <AlertTriangle className="w-4 h-4 text-red-500 mr-1" />
                          <span className="text-xs text-red-600 font-medium">{t('scale.calibRequired')}</span>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                          <span className="text-xs text-green-600">{t('scale.calibOk')}</span>
                        </div>
                      )}
                      {scale.next_calibration && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(scale.next_calibration)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => showScaleDetail(scale)}
                          className="text-gray-400 hover:text-primary transition-colors"
                          title={t('scale.titleView')}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(scale)}
                          className="text-gray-400 hover:text-primary transition-colors"
                          title={t('scale.titleEdit')}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleCopy(scale)}
                          className="text-gray-400 hover:text-green-600 transition-colors"
                          title={t('scale.titleCopy')}
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleCalibration(scale)}
                          className="text-gray-400 hover:text-primary transition-colors"
                          title={t('scale.titleCalibrate')}
                        >
                          <Wrench className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openLabelModal(scale)}
                          className="text-gray-400 hover:text-orange-600 transition-colors"
                          title={t('scale.titleLabel')}
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                        {(user?.role === 'admin' || user?.role === 'superadmin') && (
                          <button
                            onClick={() => handleDelete(scale.id)}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                            title={t('scale.titleDelete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Scale Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingScale ? t('scale.modalTitleEdit') : t('scale.modalTitleNew')}
              </h2>
              {!editingScale && scaleData.scale_name.includes(' - Kopya') && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t('scale.modalCopyNote')}
                </p>
              )}
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('scale.sectionBasic')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.labelScaleName')}
                    </label>
                    <input
                      type="text"
                      value={scaleData.scale_name}
                      onChange={(e) => setScaleData({...scaleData, scale_name: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        scaleData.scale_name && checkDuplicateName(scaleData.scale_name, editingScale?.id) 
                          ? 'border-red-300 bg-red-50' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="WAREHOUSE-SCALE-01"
                    />
                    {scaleData.scale_name && checkDuplicateName(scaleData.scale_name, editingScale?.id) && (
                      <p className="text-xs text-red-600 mt-1">{t('scale.duplicateNameError')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.labelBrand')}
                    </label>
                    <input
                      type="text"
                      value={scaleData.brand}
                      onChange={(e) => setScaleData({...scaleData, brand: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Mettler Toledo"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.labelModel')}
                    </label>
                    <input
                      type="text"
                      value={scaleData.model}
                      onChange={(e) => setScaleData({...scaleData, model: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="IND780"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.labelSerialNumber')}
                    </label>
                    <input
                      type="text"
                      value={scaleData.serial_number}
                      onChange={(e) => setScaleData({...scaleData, serial_number: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="MT2024SC001"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.labelMacAddress')}
                    </label>
                    <input
                      type="text"
                      value={scaleData.mac_address}
                      onChange={(e) => setScaleData({...scaleData, mac_address: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="BB:CC:DD:EE:FF:01"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('scale.macHint')}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.labelIpAddress')}
                    </label>
                    <input
                      type="text"
                      value={scaleData.ip_address}
                      onChange={(e) => setScaleData({...scaleData, ip_address: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        scaleData.ip_address && checkDuplicateIP(scaleData.ip_address, editingScale?.id) 
                          ? 'border-red-300 bg-red-50' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="192.168.200.10"
                    />
                    {scaleData.ip_address && checkDuplicateIP(scaleData.ip_address, editingScale?.id) && (
                      <p className="text-xs text-red-600 mt-1">{t('scale.duplicateIPError')}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Technical Specifications */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('scale.sectionTech')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.labelMaxCapacity')}
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={scaleData.max_capacity_kg}
                      onChange={(e) => setScaleData({...scaleData, max_capacity_kg: parseInt(e.target.value) || 1000})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.labelPrecision')}
                    </label>
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={scaleData.precision_grams}
                      onChange={(e) => setScaleData({...scaleData, precision_grams: parseFloat(e.target.value) || 100})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.labelProtocol')}
                    </label>
                    <select
                      value={scaleData.communication_protocol}
                      onChange={(e) => setScaleData({...scaleData, communication_protocol: e.target.value as SmartScale['communication_protocol']})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      {protocolOptions.map((protocol) => (
                        <option key={protocol.value} value={protocol.value}>
                          {protocol.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.labelStatus')}
                    </label>
                    <select
                      value={scaleData.status}
                      onChange={(e) => setScaleData({...scaleData, status: e.target.value as SmartScale['status']})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      {statusOptions.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Installation and Calibration */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('scale.sectionInstall')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.labelInstallDate')}
                    </label>
                    <input
                      type="date"
                      value={scaleData.installation_date}
                      onChange={(e) => setScaleData({...scaleData, installation_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.labelLastCalib')}
                    </label>
                    <input
                      type="date"
                      value={scaleData.calibration_date}
                      onChange={(e) => setScaleData({...scaleData, calibration_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.labelNextCalib')}
                    </label>
                    <input
                      type="date"
                      value={scaleData.next_calibration}
                      onChange={(e) => setScaleData({...scaleData, next_calibration: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Location and Assignment */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('scale.sectionLocation')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.labelCompany')}
                    </label>
                    <select
                      value={scaleData.company_id}
                      onChange={(e) => setScaleData({...scaleData, company_id: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value={0}>{t('scale.selectCompany')}</option>
                      {companiesData.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.labelLocation')}
                    </label>
                    <select
                      value={scaleData.location_id}
                      onChange={(e) => setScaleData({...scaleData, location_id: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value={0}>{t('scale.selectLocation')}</option>
                      {locationsData.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.labelAdmin')}
                    </label>
                    <select
                      value={scaleData.assigned_admin_id}
                      onChange={(e) => setScaleData({...scaleData, assigned_admin_id: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value={0}>{t('scale.selectAdmin')}</option>
                      {usersData.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.labelPurchasePrice')}
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="number"
                        step="0.01"
                        value={scaleData.purchase_price}
                        onChange={(e) => setScaleData({...scaleData, purchase_price: parseFloat(e.target.value) || 0})}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="15000"
                      />
                      <select
                        value={scaleData.currency}
                        onChange={(e) => setScaleData({...scaleData, currency: e.target.value as 'TRY' | 'USD' | 'EUR'})}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        <option value="TRY">₺</option>
                        <option value="USD">$</option>
                        <option value="EUR">€</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('scale.labelNotes')}
                </label>
                <textarea
                  value={scaleData.notes}
                  onChange={(e) => setScaleData({...scaleData, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder={t('scale.notesPlaceholder')}
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
              >
                {editingScale ? t('scale.update') : t('common.save')}
              </button>
            </div>
          </div>
        </Modal>

      {/* Scale Detail Modal */}
      <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)}>
          {selectedScale && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('scale.detailTitle')}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {selectedScale.scale_name} - {selectedScale.brand} {selectedScale.model}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Scale Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('scale.detailSectionScale')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('scale.detailScaleName')}</span>
                      <span className="font-medium">{selectedScale.scale_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('scale.detailBrandModel')}</span>
                      <span className="font-medium">{selectedScale.brand} {selectedScale.model}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('scale.detailSerialNo')}</span>
                      <span className="font-medium">{selectedScale.serial_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('scale.detailCapacity')}</span>
                      <span className="font-medium">{selectedScale.max_capacity_kg} kg</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('scale.detailPrecision')}</span>
                      <span className="font-medium">{selectedScale.precision_grams}g</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('scale.detailSectionNetwork')}</h3>
                  <div className="space-y-3 text-sm">
                    {selectedScale.ip_address && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('scale.detailIpAddress')}</span>
                        <span className="font-medium">{selectedScale.ip_address}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('scale.detailMacAddress')}</span>
                      <span className="font-medium">{selectedScale.mac_address}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('scale.detailProtocol')}</span>
                      <span className="font-medium capitalize">
                        {protocolOptions.find(p => p.value === selectedScale.communication_protocol)?.label}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Calibration Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('scale.detailSectionCalib')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {selectedScale.calibration_date && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('scale.detailLastCalib')}</span>
                      <span className="font-medium">{formatDate(selectedScale.calibration_date)}</span>
                    </div>
                  )}
                  {selectedScale.next_calibration && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('scale.detailNextCalib')}</span>
                      <span className={`font-medium ${isCalibrationDue(selectedScale.next_calibration) ? 'text-orange-600' : ''}`}>
                        {formatDate(selectedScale.next_calibration)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('scale.detailInstallDate')}</span>
                    <span className="font-medium">{formatDate(selectedScale.installation_date)}</span>
                  </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('scale.detailAdmin')}</span>
                      <span className="font-medium">{selectedScale.assigned_admin?.name || t('scale.detailUnassigned')}</span>
                    </div>
                </div>
              </div>

              {/* Notes */}
              {selectedScale.notes && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('scale.detailSectionNotes')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    {selectedScale.notes}
                  </p>
                </div>
              )}

              {/* Action Buttons - Calibration */}
              <div className="flex justify-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => handleCalibration(selectedScale)}
                  className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  <Wrench className="w-4 h-4 mr-2" />
                  {t('scale.calibrateBtn')}
                </button>
                <button
                  onClick={() => handleCalibrationHistory(selectedScale)}
                  className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  {t('scale.calibHistoryBtn')}
                </button>
              </div>
            </div>
          </div>
          )}
        </Modal>

      {/* Calibration History Modal */}
      <Modal isOpen={showCalibrationHistory} onClose={() => setShowCalibrationHistory(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('scale.calibHistoryTitle')}</h2>
                <button
                  onClick={() => setShowCalibrationHistory(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                {calibrationHistory.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Settings className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>{t('scale.calibHistoryEmpty')}</p>
                  </div>
                ) : (
                  calibrationHistory.map((record) => (
                    <div key={record.id} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {formatDate(record.calibration_date)}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {record.calibrated_by_name}
                        </div>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {t('scale.calibHistoryType')} {record.calibration_type || t('scale.calibHistoryManual')}
                        </div>
                        <div className={`text-xs px-2 py-1 rounded-full ${
                          record.status === 'completed' ? 'bg-green-100 text-green-800' :
                          record.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                        }`}>
                          {record.status === 'completed' ? t('scale.calibHistoryCompleted') :
                           record.status === 'pending' ? t('scale.calibHistoryPending') : record.status}
                        </div>
                      </div>
                      {record.notes && (
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {record.notes}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
              
              {/* Add Calibration Button */}
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-center">
                  <button
                    onClick={handleCalibrationConfirm}
                    className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    {t('scale.calibHistoryNewBtn')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal>

      {/* Calibration Modal */}
      <Modal isOpen={showCalibrationModal} onClose={() => setShowCalibrationModal(false)}>
          {selectedScale && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('scale.calibModalTitle')}</h2>
                <button
                  onClick={() => setShowCalibrationModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  ✕
                </button>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {selectedScale.scale_name} - {selectedScale.brand} {selectedScale.model}
              </p>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Kalibrasyon Bilgileri */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('scale.calibSectionInfo')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.calibLabelCompany')}
                    </label>
                    <input
                      type="text"
                      value={calibrationData.calibration_company}
                      onChange={(e) => setCalibrationData({...calibrationData, calibration_company: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="Calibration Services Ltd."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.calibLabelNumber')}
                    </label>
                    <input
                      type="text"
                      value={calibrationData.calibration_number}
                      onChange={(e) => setCalibrationData({...calibrationData, calibration_number: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="KAL-2024-001"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.calibLabelDate')}
                    </label>
                    <input
                      type="date"
                      value={calibrationData.calibration_date}
                      onChange={(e) => setCalibrationData({...calibrationData, calibration_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.calibLabelNextDate')}
                    </label>
                    <input
                      type="date"
                      value={calibrationData.next_calibration_date}
                      onChange={(e) => setCalibrationData({...calibrationData, next_calibration_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.calibLabelType')}
                    </label>
                    <select
                      value={calibrationData.calibration_type}
                      onChange={(e) => setCalibrationData({...calibrationData, calibration_type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="manual">{t('scale.calibTypeManual')}</option>
                      <option value="automatic">{t('scale.calibTypeAutomatic')}</option>
                      <option value="certified">{t('scale.calibTypeCertified')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('scale.calibLabelStatus')}
                    </label>
                    <select
                      value={calibrationData.status}
                      onChange={(e) => setCalibrationData({...calibrationData, status: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="completed">{t('scale.calibStatusCompleted')}</option>
                      <option value="pending">{t('scale.calibStatusPending')}</option>
                      <option value="failed">{t('scale.calibStatusFailed')}</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('scale.calibLabelNotes')}
                  </label>
                  <textarea
                    value={calibrationData.notes}
                    onChange={(e) => setCalibrationData({...calibrationData, notes: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder={t('scale.calibNotesPlaceholder')}
                  />
                </div>
              </div>

              {/* Kalibrasyon Önemi Bilgisi */}
              <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                <h4 className="text-sm font-medium text-primary-dark mb-2">{t('scale.calibImportanceTitle')}</h4>
                <div className="text-xs text-primary-dark space-y-1">
                  <p>{t('scale.calibImportance1')}</p>
                  <p>{t('scale.calibImportance2')}</p>
                  <p>{t('scale.calibImportance3')}</p>
                  <p>{t('scale.calibImportance4')}</p>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowCalibrationModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCalibrationSave}
                className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
              >
                <Wrench className="w-4 h-4 mr-2" />
                {t('scale.calibSaveBtn')}
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* Scale Label Modal */}
      <Modal isOpen={showLabelModal} onClose={() => setShowLabelModal(false)}>
          {selectedScaleForLabel && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('scale.labelModalTitle')}
                </h2>
                <button
                  onClick={() => setShowLabelModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  ✕
                </button>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {selectedScaleForLabel.scale_name} - {selectedScaleForLabel.brand} {selectedScaleForLabel.model}
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('scale.labelWidth')}
                  </label>
                  <input
                    type="number"
                    value={labelSettings.width}
                    onChange={(e) => setLabelSettings({...labelSettings, width: parseInt(e.target.value) || 100})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    min="50"
                    max="200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('scale.labelHeight')}
                  </label>
                  <input
                    type="number"
                    value={labelSettings.height}
                    onChange={(e) => setLabelSettings({...labelSettings, height: parseInt(e.target.value) || 75})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    min="30"
                    max="150"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('scale.labelOrientation')}
                </label>
                <select
                  value={labelSettings.orientation}
                  onChange={(e) => setLabelSettings({...labelSettings, orientation: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="portrait">{t('scale.labelPortrait')}</option>
                  <option value="landscape">{t('scale.labelLandscape')}</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('scale.labelFontSize')}
                  </label>
                  <input
                    type="number"
                    value={labelSettings.fontSize}
                    onChange={(e) => setLabelSettings({...labelSettings, fontSize: parseInt(e.target.value) || 8})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    min="6"
                    max="16"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('scale.labelMargin')}
                  </label>
                  <input
                    type="number"
                    value={labelSettings.margin}
                    onChange={(e) => setLabelSettings({...labelSettings, margin: parseInt(e.target.value) || 2})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    min="1"
                    max="10"
                  />
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('scale.labelPresets')}</h4>
                <div className="space-y-2">
                  <button
                    onClick={() => setLabelSettings({width: 100, height: 75, orientation: 'portrait', fontSize: 8, margin: 2})}
                    className="w-full text-left px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700"
                  >
                    Zebra ZT411 (100x75mm)
                  </button>
                  <button
                    onClick={() => setLabelSettings({width: 80, height: 60, orientation: 'portrait', fontSize: 7, margin: 2})}
                    className="w-full text-left px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700"
                  >
                    {t('scale.presetStandard')}
                  </button>
                  <button
                    onClick={() => setLabelSettings({width: 120, height: 80, orientation: 'landscape', fontSize: 9, margin: 3})}
                    className="w-full text-left px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700"
                  >
                    {t('scale.presetLarge')}
                  </button>
                </div>
              </div>

              {/* Önizleme */}
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('scale.labelInfoTitle')}</h4>
                <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                  <div><strong>{t('scale.labelInfoScaleName')}</strong> {selectedScaleForLabel.scale_name}</div>
                  <div><strong>{t('scale.labelInfoBrandModel')}</strong> {selectedScaleForLabel.brand} {selectedScaleForLabel.model}</div>
                  {selectedScaleForLabel.ip_address && <div><strong>{t('scale.labelInfoIp')}</strong> {selectedScaleForLabel.ip_address}</div>}
                  <div><strong>{t('scale.labelInfoSerial')}</strong> {selectedScaleForLabel.serial_number}</div>
                  <div><strong>{t('scale.labelInfoCapacity')}</strong> {selectedScaleForLabel.max_capacity_kg} kg</div>
                  <div><strong>{t('scale.labelInfoQr')}</strong> {t('scale.labelInfoQrDesc')}</div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowLabelModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => {
                  printScaleLabel(selectedScaleForLabel);
                  setShowLabelModal(false);
                }}
                className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
              >
                <Printer className="w-4 h-4 mr-2" />
                {t('scale.labelPrintBtn')}
              </button>
            </div>
          </div>
          )}
        </Modal>
    </div>
  );
};

export default SmartScalesPage;